import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface Eligibility {
  eligibilityId: string;
  invoiceNumber: string;
  invoiceDate: string;
  netPayableAmount: number;
  paymentStatusCode: string;
}

/**
 * The stages a payment eligibility passes through. Offered as a filter because
 * the list endpoint takes exactly one status, and a bill that has already moved
 * on would otherwise look as if it did not exist.
 */
const STATUSES = [
  'PAYMENT_ELIGIBLE',
  'PAYMENT_PROPOSAL_CREATED',
  'PAYMENT_APPROVED',
  'PAYMENT_INITIATED',
  'PAYMENT_PROCESSED',
  'PAYMENT_CONFIRMED',
  'SETTLEMENT_COMPLETED',
  'PAYMENT_CLOSED',
  'ON_HOLD',
];

interface Props {
  readonly value: string;
  readonly onPick: (eligibilityId: string) => void;
}

/**
 * Picks a payment eligibility from the queue instead of asking for its uuid.
 *
 * The finance detail screens are all keyed by eligibility id, which a user has
 * no way of typing from memory; this turns them into something that can be
 * opened from the screen itself.
 */
export function EligibilityPicker({ value, onPick }: Props): ReactNode {
  const { day } = useBusinessDay();
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const [status, setStatus] = useState(STATUSES[0]);
  const [items, setItems] = useState<Eligibility[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    apiRequest<Eligibility[]>(`/v1/payments/eligibility/status/${status}`, { token })
      .then((r) => { if (alive) setItems(Array.isArray(r) ? r : []); })
      .catch((e) => { if (alive) { setItems([]); setError((e as Error).message); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [status, token]);

  const label = (e: Eligibility) =>
    [e.invoiceNumber, day(e.invoiceDate), `₹${money(e.netPayableAmount)}`].join(' · ');

  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
      <label htmlFor="elig-status">{t('finance.picker.stage')}</label>
      <select
        id="elig-status"
        data-testid="elig-status"
        value={status}
        onChange={(e) => setStatus(e.target.value)}
        style={{ padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default)' }}
      >
        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>

      <label htmlFor="elig-select">{t('finance.picker.bill')}</label>
      <select
        id="elig-select"
        data-testid="elig-select"
        value={value}
        onChange={(e) => onPick(e.target.value)}
        style={{ flex: 1, minWidth: 240, padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default)' }}
      >
        <option value="">
          {loading
            ? t('finance.picker.loading')
            : items.length === 0
              ? t('finance.picker.none')
              : t('finance.picker.choose')}
        </option>
        {items.map((e) => (
          <option key={e.eligibilityId} value={e.eligibilityId}>{label(e)}</option>
        ))}
      </select>

      {error ? <span role="alert" style={{ color: 'red' }}>{error}</span> : null}
    </div>
  );
}
